import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatDividerModule } from "@angular/material/divider";
import { MatCardModule } from "@angular/material/card";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";

import {
  ExportConfig,
  ExportFormat,
  ExportProgress,
  ExportResult,
  SearchQuery,
  SearchQueryDisplay,
  SearchType,
} from "../../model";
import { SearchQueryService } from "../../services/search";

/**
 * Dialog component for exporting search results in various formats
 * Supports CSV, GeoJSON Point, and GeoJSON Polygon exports with automatic pagination
 */
@Component({
  selector: "app-search-export-dialog",
  templateUrl: "./search-export-dialog.component.html",
  styleUrls: ["./search-export-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,

  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDividerModule,
    MatCardModule,
  ],
})
export class SearchExportDialogComponent implements OnInit {
  /** Dialog title */
  readonly title = "Export Search Results";

  /** Dialog description */
  readonly description = "Export your search results in various formats for download and analysis";

  /** Configuration received from parent */
  config: Required<ExportConfig>;

  /** Form for export parameters */
  exportForm: FormGroup;

  /** Available export formats */
  readonly exportFormats: ExportFormat[] = [
    {
      id: "csv",
      label: "CSV File",
      description: "Tabular data format compatible with Excel and spreadsheet applications",
      icon: "table_chart",
      fileExtension: "csv",
      recommended: true,
    },
    {
      id: "geojson-point",
      label: "GeoJSON Points",
      description: "Geographic data with point coordinates from geometry centroids",
      icon: "place",
      fileExtension: "geojson",
    },
    {
      id: "geojson-polygon",
      label: "GeoJSON Polygons",
      description: "Geographic data preserving original geometry shapes",
      icon: "polyline",
      fileExtension: "geojson",
    },
  ];

  /** Selected export format */
  selectedFormat: ExportFormat | null = null;

  /** Export progress state */
  exportProgress: ExportProgress = {
    isExporting: false,
    progress: 0,
    statusMessage: "",
    completed: false,
  };

  /** Search query display information */
  searchQueryDisplay: SearchQueryDisplay;

  /** Number of results currently loaded */
  loadedResultsCount: number = 0;

  /** Default configuration values */
  private readonly defaultMaxRecords = 100;

  private readonly maxAllowedRecords = 1000;

  constructor(
    private dialogRef: MatDialogRef<SearchExportDialogComponent>,
    private cdr: ChangeDetectorRef,
    private searchQueryService: SearchQueryService,
    @Inject(MAT_DIALOG_DATA) private dialogData: ExportConfig,
  ) {
    // Extract loaded results count from dialog data
    this.loadedResultsCount = this.dialogData.loadedResultsCount || 0;

    // Calculate max records based on current search state
    const smartDefault = this.loadedResultsCount > 0 ? this.loadedResultsCount : this.defaultMaxRecords;

    // Set up configuration
    this.config = {
      defaultMaxRecords: smartDefault,
      maxAllowedRecords: this.maxAvailableRecords,
      availableFormats: this.exportFormats,
      defaultFormat: this.exportFormats[0],
      loadedResultsCount: this.loadedResultsCount,
      ...this.dialogData,
    };

    // Initialize form
    this.exportForm = new FormGroup({
      maxRecords: new FormControl(smartDefault, [
        Validators.required,
        Validators.min(1),
        Validators.max(this.maxAvailableRecords),
      ]),
    });

    // Generate search query display information
    this.searchQueryDisplay = this.generateSearchQueryDisplay(this.config.searchQuery);

    // Set default format
    this.selectedFormat = this.config.defaultFormat;
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  /**
   * Initialize component state
   */
  private initializeComponent(): void {
    this.cdr.detectChanges();
  }

  /**
   * Get maximum available records based on search state
   */
  get maxAvailableRecords(): number {
    /*const searchState = this.searchQueryService.getCurrentSearchState();

    // If no more results available, limit to current count
    if (!searchState.hasMore && searchState.results.length > 0) {
      return searchState.results.length;
    }*/

    return this.maxAllowedRecords;
  }

  /**
   * Generate display information for the search query
   */
  private generateSearchQueryDisplay(searchQuery: SearchQuery): SearchQueryDisplay {
    switch (searchQuery.searchType) {
      case SearchType.TEXT:
        return {
          type: "text",
          title: "Text Search",
          description: `Query: "${searchQuery.query}"`,
          preview: searchQuery.query,
          icon: "search",
        };

      case SearchType.IMAGE_URL:
        return {
          type: "image-url",
          title: "Image URL Search",
          description: "Search results based on image from URL",
          preview: searchQuery.imageUrl,
          icon: "link",
        };

      case SearchType.IMAGE_DATA:
        return {
          type: "image-data",
          title: "Uploaded Image Search",
          description: "Search results based on uploaded image",
          preview: searchQuery.imagePreview,
          icon: "file_upload",
        };

      case SearchType.LOCATION: // Location coordinate search
        return {
          type: "location",
          title: "Location Search",
          description: searchQuery.location
            ? `Location: ${searchQuery.location[0]}, ${searchQuery.location[1]}${searchQuery.radiusMeters ? ` (${searchQuery.radiusMeters}m radius)` : ""}`
            : "Location-based search",
          preview: searchQuery.location?.join(", "),
          icon: "place",
        };
      default:
        return {
          type: "text",
          title: "Search Results",
          description: "Export search results",
          icon: "search",
        };
    }
  }

  /**
   * Check if export can be performed
   */
  get canExport(): boolean {
    return this.exportForm.valid && this.selectedFormat !== null && !this.exportProgress.isExporting;
  }

  /**
   * Check if there's an error state
   */
  get hasError(): boolean {
    return !!this.exportProgress.error;
  }

  /**
   * Check if export completed successfully
   */
  get isCompleted(): boolean {
    return this.exportProgress.completed && !this.exportProgress.error;
  }

  /**
   * Check if export completed successfully
   */
  get isWarning(): boolean {
    return this.exportProgress.warning && !this.exportProgress.error;
  }

  /**
   * Get the button text for the export action
   */
  get exportButtonText(): string {
    if (this.exportProgress.isExporting) {
      return "Exporting...";
    }
    return "Export Results";
  }

  get searchQuery(): SearchQuery {
    return this.config.searchQuery;
  }

  /**
   * Dynamic hint message for max records field
   */

  /**
   * Whether to show limitation warning
   */

  /**
   * Effective maximum for the number input
   */

  /**
   * Select an export format
   */
  selectFormat(format: ExportFormat): void {
    this.selectedFormat = format;
    this.cdr.detectChanges();
  }

  /**
   * Start the export process
   */
  async onExport(): Promise<void> {
    if (!this.canExport || !this.selectedFormat) {
      return;
    }

    const maxRecords = this.exportForm.get("maxRecords")?.value;

    // Reset progress state for new export
    this.exportProgress = {
      isExporting: true,
      progress: 0,
      statusMessage: "Initializing export...",
      completed: false,
      error: undefined,
      downloadedCount: 0,
      totalCount: maxRecords,
    };
    this.dialogRef.disableClose = true;
    this.cdr.detectChanges();

    try {
      // Determine format for API call
      let apiFormat: "csv" | "geojson-polygon" | "geojson-point";
      switch (this.selectedFormat.id) {
        case "csv":
          apiFormat = "csv";
          break;
        case "geojson-point":
          apiFormat = "geojson-point";
          break;
        case "geojson-polygon":
          apiFormat = "geojson-polygon";
          break;
        default:
          throw new Error(`Unsupported export format: ${this.selectedFormat.id}`);
      }

      // Create progress callback with smart status messages
      const progressCallback = (downloaded: number, total: number) => {
        this.exportProgress.downloadedCount = downloaded;
        this.exportProgress.totalCount = total;
        this.exportProgress.progress = Math.round((downloaded / total) * 90); // Reserve 10% for processing

        // Smart status message based on whether we're reusing or fetching data
        if (downloaded <= this.loadedResultsCount) {
          this.exportProgress.statusMessage = `Reusing ${downloaded} already loaded results...`;
        } else if (this.loadedResultsCount > 0) {
          const additional = downloaded - this.loadedResultsCount;
          this.exportProgress.statusMessage = `Reusing ${this.loadedResultsCount} loaded results and fetching ${additional} more...`;
        } else {
          this.exportProgress.statusMessage = `Downloading results: ${downloaded} of ${total}`;
        }
        this.cdr.detectChanges();
      };

      // Call the optimized export service with progress callback
      const exportData = await new Promise((resolve, reject) => {
        this.searchQueryService.exportSearchResults(apiFormat, maxRecords, progressCallback).subscribe({
          next: (data) => {
            this.exportProgress.progress = 95;
            this.exportProgress.statusMessage = "Processing data for download...";
            this.cdr.detectChanges();
            resolve(data);
          },
          error: (error) => {
            console.error("Export failed:", error);
            reject(error);
          },
        });
      });

      // Process data based on selected format
      let processedData: any;
      let filename: string;

      switch (this.selectedFormat.id) {
        case "csv":
          processedData = exportData;
          filename = `search-export-${this.getCurrentTimestamp()}.csv`;
          break;

        case "geojson-point":
          processedData = exportData;
          filename = `search-export-points-${this.getCurrentTimestamp()}.geojson`;
          break;

        case "geojson-polygon":
          processedData = exportData; // Already in GeoJSON format from API
          filename = `search-export-polygons-${this.getCurrentTimestamp()}.geojson`;
          break;
      }

      // Trigger download
      this.downloadFile(processedData, filename);

      // Complete export
      this.exportProgress = {
        isExporting: false,
        progress: 100,
        statusMessage: `Export completed! Downloaded ${this.exportProgress.downloadedCount} results as ${filename}`,
        completed: true,
        warning: this.exportProgress.downloadedCount !== maxRecords,
        downloadedCount: this.exportProgress.downloadedCount,
        totalCount: maxRecords,
      };
      this.dialogRef.disableClose = false;
      this.cdr.detectChanges();
    } catch (error) {
      console.error("Export error:", error);
      this.exportProgress = {
        isExporting: false,
        progress: 0,
        statusMessage: "Export failed",
        completed: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
        downloadedCount: 0,
        totalCount: maxRecords,
      };
      this.dialogRef.disableClose = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Get current timestamp for filename
   */
  private getCurrentTimestamp(): string {
    return new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  }

  /**
   * Download a file with given content
   */
  private downloadFile(content: any, filename: string): void {
    const dataStr = typeof content === "string" ? content : JSON.stringify(content, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/octet-stream" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(dataBlob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up object URL
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  /**
   * Cancel the dialog
   */
  onCancel(): void {
    const result: ExportResult = {
      action: "cancel",
    };
    this.dialogRef.close(result);
  }
}
